import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchMyInvites,
  acceptInvite,
  declineInvite,
} from '../api/invites';
import { userKeys } from './useUser';
import { AUTH_QUERY_KEY } from '@/lib/auth';
import type { User } from '@/domain/authentication';

/** Fetches all pending invitations for the current user. */
export function useMyInvites(enabled = true) {
  return useQuery({
    queryKey: userKeys.myInvites(),
    queryFn: fetchMyInvites,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled,
  });
}

/** Accepts a pending invitation and invalidates the invites list, auth state, and user contacts on success. */
export function useAcceptInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inviteId: string) => acceptInvite(inviteId),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: userKeys.myInvites() });
      queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
      
      // Get the current user to invalidate their contacts query
      const authData = queryClient.getQueryData<User | null | undefined>(AUTH_QUERY_KEY);
      if (authData?.id) {
        queryClient.invalidateQueries({ queryKey: userKeys.contacts(authData.id) });
      }
    },
  });
}

/** Declines a pending invitation and invalidates the invites list and auth state on success. */
export function useDeclineInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inviteId: string) => declineInvite(inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.myInvites() });
    },
  });
}
